/**
 * Equipment icon mapping — resolves an equipment code or description to an icon key.
 *
 * Resolution order:
 *   1. Match equipment code prefix (first match wins)
 *   2. Fall back to keyword matching in description
 *   3. Return 'default'
 */

export type EquipmentIconKey =
  | 'airCompressor'
  | 'attachment'
  | 'atv'
  | 'backhoe'
  | 'container'
  | 'directionalDrill'
  | 'dozer'
  | 'excavator'
  | 'loader'
  | 'forklift'
  | 'fusionMachine'
  | 'tractor'
  | 'generator'
  | 'grader'
  | 'manLift'
  | 'misc'
  | 'welder'
  | 'pipelayer'
  | 'crawlerCarrier'
  | 'pressureWasher'
  | 'pressurePump'
  | 'mower'
  | 'compactor'
  | 'skidSteer'
  | 'suv'
  | 'threadingMachine'
  | 'truck'
  | 'waterTruck'
  | 'trailer'
  | 'trencher'
  | 'toolVan'
  | 'default';

// ── Code prefix → icon key (order matters — first match wins) ──
// Add more mappings here as new equipment codes appear.
const equipmentCodeToIconKey: Array<{ pattern: RegExp; key: EquipmentIconKey }> = [
  { pattern: /^AC-/i, key: 'airCompressor' },
  { pattern: /^AT-/i, key: 'attachment' },
  { pattern: /^ATV-/i, key: 'atv' },
  { pattern: /^BH-/i, key: 'backhoe' },
  { pattern: /^CB-/i, key: 'directionalDrill' },
  { pattern: /^CO-/i, key: 'misc' },
  { pattern: /^CT-185CFM/i, key: 'airCompressor' },
  { pattern: /^CT-TL-185/i, key: 'airCompressor' },
  { pattern: /^CT-TST-CAB/i, key: 'trailer' },
  { pattern: /^CT-VACTRON/i, key: 'trailer' },
  { pattern: /^CX-/i, key: 'container' },
  { pattern: /^DD-/i, key: 'directionalDrill' },
  { pattern: /^DZ-/i, key: 'dozer' },
  { pattern: /^EX-/i, key: 'excavator' },
  { pattern: /^FL-3\.0YD/i, key: 'loader' },
  { pattern: /^FL-4\.5YD/i, key: 'loader' },
  { pattern: /^FL-5-8K/i, key: 'forklift' },
  { pattern: /^FL-RT-8K/i, key: 'forklift' },
  { pattern: /^FL-TH-/i, key: 'forklift' },
  { pattern: /^FM-/i, key: 'fusionMachine' },
  { pattern: /^FT-/i, key: 'tractor' },
  { pattern: /^GN-/i, key: 'generator' },
  { pattern: /^HN-/i, key: 'misc' },
  { pattern: /^ME-MISC/i, key: 'misc' },
  { pattern: /^ME-WM-/i, key: 'welder' },
  { pattern: /^MG-/i, key: 'grader' },
  { pattern: /^ML-/i, key: 'manLift' },
  { pattern: /^MOW-/i, key: 'mower' },
  { pattern: /^PL-/i, key: 'pipelayer' },
  { pattern: /^PN-/i, key: 'crawlerCarrier' },
  { pattern: /^PU-PS-/i, key: 'pressurePump' },
  { pattern: /^PW-/i, key: 'pressureWasher' },
  { pattern: /^RM-/i, key: 'mower' },
  { pattern: /^SC-/i, key: 'compactor' },
  { pattern: /^SS-/i, key: 'skidSteer' },
  { pattern: /^SUV-/i, key: 'suv' },
  { pattern: /^TH\d+/i, key: 'threadingMachine' },
  { pattern: /^TK-WT-/i, key: 'waterTruck' },
  { pattern: /^TK-/i, key: 'truck' },
  { pattern: /^TL-/i, key: 'trailer' },
  { pattern: /^TR-/i, key: 'trencher' },
  { pattern: /^VB-/i, key: 'toolVan' },
  // Add more code prefixes here ↑
];

// ── Description keyword → icon key (order matters — first match wins) ──
// Add more keyword patterns here as needed.
const descriptionToIconKey: Array<{ pattern: RegExp; key: EquipmentIconKey }> = [
  { pattern: /air compressor|a\/comp/i, key: 'airCompressor' },
  { pattern: /hammer attachment|attachment/i, key: 'attachment' },
  { pattern: /\batv\b/i, key: 'atv' },
  { pattern: /backhoe/i, key: 'backhoe' },
  { pattern: /conex|container/i, key: 'container' },
  { pattern: /directional drill|drill/i, key: 'directionalDrill' },
  { pattern: /dozer/i, key: 'dozer' },
  { pattern: /excavator/i, key: 'excavator' },
  { pattern: /wheel loader|front end loader/i, key: 'loader' },
  { pattern: /forklift|telehandler/i, key: 'forklift' },
  { pattern: /fusion machine/i, key: 'fusionMachine' },
  { pattern: /farm tractor/i, key: 'tractor' },
  { pattern: /generator|light plant/i, key: 'generator' },
  { pattern: /motor grader/i, key: 'grader' },
  { pattern: /man lift|manlift/i, key: 'manLift' },
  { pattern: /welding machine|weld rig|weld/i, key: 'welder' },
  { pattern: /pipelayer|sideboom/i, key: 'pipelayer' },
  { pattern: /crawler/i, key: 'crawlerCarrier' },
  { pattern: /pressure washer/i, key: 'pressureWasher' },
  { pattern: /pressure pump/i, key: 'pressurePump' },
  { pattern: /weedeater|lawnmower/i, key: 'mower' },
  { pattern: /compactor/i, key: 'compactor' },
  { pattern: /skidsteer|skid steer|skid/i, key: 'skidSteer' },
  { pattern: /\bsuv\b/i, key: 'suv' },
  { pattern: /threading machine/i, key: 'threadingMachine' },
  { pattern: /water truck/i, key: 'waterTruck' },
  { pattern: /\btruck\b/i, key: 'truck' },
  { pattern: /\btrailer\b/i, key: 'trailer' },
  { pattern: /trencher/i, key: 'trencher' },
  { pattern: /tool van/i, key: 'toolVan' },
  { pattern: /misc/i, key: 'misc' },
  // Add more description keywords here ↑
];

/**
 * Resolve an equipment code and/or description to an icon key.
 *
 * @param code        Equipment code (e.g. "EX-200", "TK-WT-4000")
 * @param description Equipment description or classification name
 * @returns           The best-matching EquipmentIconKey
 */
export function getEquipmentIconKey(
  code?: string | null,
  description?: string | null
): EquipmentIconKey {
  // 1. Try code prefix match
  if (code) {
    for (const { pattern, key } of equipmentCodeToIconKey) {
      if (pattern.test(code)) return key;
    }
  }

  // 2. Try description keyword match
  if (description) {
    for (const { pattern, key } of descriptionToIconKey) {
      if (pattern.test(description)) return key;
    }
  }

  // 3. Default fallback
  return 'default';
}
